import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  IconButton,
  Stack,
  FormControlLabel,
  Switch,
  Tooltip,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  CheckCircle,
  ErrorOutline,
  ContentCopy,
  Archive,
  Speed,
  Timer,
  Replay,
} from '@mui/icons-material';
import { uploadAPI, UploadOptions, UploadProgress, ANON_MAX_FILE_SIZE, REGISTERED_MAX_FILE_SIZE } from '../../api/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';

type FileState = {
  file: File;
  status: 'waiting' | 'uploading' | 'success' | 'error';
  error?: string;
  fileId?: string;
  progress: UploadProgress;
};

const emptyProgress: UploadProgress = { percentage: 0, bytesUploaded: 0, totalBytes: 0, speed: 0 };

const fmtSize = (b: number): string => {
  if (b === 0) return '0 B';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
};

const fmtSpeed = (bps: number): string => {
  if (bps === 0) return '--';
  if (bps < 1024 * 1024) return (bps / 1024).toFixed(0) + ' KB/s';
  return (bps / (1024 * 1024)).toFixed(1) + ' MB/s';
};

const fmtETA = (bytesLeft: number, speed: number): string => {
  if (speed <= 0) return '...';
  const secs = bytesLeft / speed;
  if (secs < 60) return `${Math.ceil(secs)}s`;
  if (secs < 3600) return `${Math.ceil(secs / 60)}m ${Math.ceil(secs % 60)}s`;
  return `${Math.floor(secs / 3600)}h ${Math.ceil((secs % 3600) / 60)}m`;
};

const FileUploadArea: React.FC = () => {
  const [fileState, setFileState] = useState<FileState | null>(null);
  const [expiryTime, setExpiryTime] = useState('1d');
  const [oneTimeDownload, setOneTimeDownload] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) {
      setFileState({
        file: accepted[0],
        status: 'waiting',
        progress: { ...emptyProgress, totalBytes: accepted[0].size },
      });
    }
  }, []);

  const maxFileBytes = isAuthenticated ? REGISTERED_MAX_FILE_SIZE : ANON_MAX_FILE_SIZE;
  const maxFileLabel = isAuthenticated ? '2GB' : '500MB';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      const tooLarge = rejections.some((r) => r.errors.some((e) => e.code === 'file-too-large'));
      if (tooLarge) {
        enqueueSnackbar(`File exceeds the ${maxFileLabel} limit`, { variant: 'error' });
      }
    },
    multiple: false,
    maxSize: maxFileBytes,
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/x-7z-compressed': ['.7z'],
      'application/gzip': ['.gz'],
      'application/x-tar': ['.tar'],
    },
  });

  const handleUpload = async () => {
    if (!fileState || fileState.status === 'uploading' || fileState.status === 'success') return;
    if (fileState.file.size > maxFileBytes) {
      const message = `File exceeds the ${maxFileLabel} limit`;
      setFileState((prev) => prev ? { ...prev, status: 'error', error: message } : prev);
      enqueueSnackbar(message, { variant: 'error' });
      return;
    }
    setFileState((prev) => prev ? { ...prev, status: 'uploading', progress: { ...emptyProgress, totalBytes: prev.file.size } } : prev);

    try {
      const opts: UploadOptions = {
        expiryTime: isAuthenticated ? expiryTime : '1d',
        oneTimeDownload: isAuthenticated ? oneTimeDownload : false,
        onProgress: (progress) => setFileState((prev) => prev ? { ...prev, progress } : prev),
      };
      const res = await uploadAPI.uploadFile(fileState.file, opts);
      setFileState((prev) => prev ? {
        ...prev, status: 'success', fileId: res?.data?.fileId,
        progress: { ...prev.progress, percentage: 100, bytesUploaded: prev.file.size },
      } : prev);
      enqueueSnackbar('Upload complete!', { variant: 'success' });
    } catch (err: any) {
      setFileState((prev) => prev ? { ...prev, status: 'error', error: err.response?.data?.error || 'Upload failed' } : prev);
      enqueueSnackbar('Upload failed', { variant: 'error' });
    }
  };

  const reset = () => setFileState(null);
  const copyLink = () => {
    if (!fileState?.fileId) return;
    navigator.clipboard.writeText(`${window.location.origin}/download/${fileState.fileId}/${encodeURIComponent(fileState.file.name)}`);
    enqueueSnackbar('Link copied!', { variant: 'info' });
  };

  const f = fileState;
  const p = f?.progress || emptyProgress;

  return (
    <Box>
      {/* Hero Title */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 5 } }} className="fade-in">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.6rem', sm: '2.4rem' },
            letterSpacing: '-0.03em',
            mb: 1,
          }}
        >
          Upload your{' '}
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(135deg, #7C6FFF, #B794FF, #FF6B8A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            files
          </Box>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, maxWidth: 480, mx: 'auto' }}>
          Fast, secure file sharing with automatic expiration
        </Typography>
      </Box>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: 620,
          mx: 'auto',
          borderRadius: 5,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg, #7C6FFF, #B794FF, #FF6B8A)',
          },
        }}
        className="slide-up"
      >
        {!f ? (
          /* ---- DROPZONE ---- */
          <Box
            {...getRootProps()}
            sx={{
              py: { xs: 6, sm: 8 },
              px: { xs: 3, sm: 4 },
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              ...(isDragActive && {
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                animation: 'dropzonePulse 1.5s ease infinite',
              }),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                '& .upload-icon-box': { animation: 'float 2s ease-in-out infinite', boxShadow: '0 12px 32px rgba(124,111,255,0.2)' },
              },
            }}
          >
            <input {...getInputProps()} />
            <Box
              className="upload-icon-box"
              sx={{
                width: 88, height: 88, borderRadius: '24px',
                background: 'linear-gradient(135deg, rgba(124,111,255,0.12), rgba(255,107,138,0.08))',
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 3,
                transition: 'all 0.4s ease',
              }}
            >
              <CloudUpload sx={{ fontSize: 38, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {isDragActive ? 'Release to upload' : 'Drop your archive here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              or click to browse your files
            </Typography>
            <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap>
              {['.zip', '.rar', '.7z', '.tar.gz'].map((ext) => (
                <Chip
                  key={ext}
                  label={ext}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: 24,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    color: 'text.secondary',
                  }}
                />
              ))}
              <Chip
                label={`max ${maxFileLabel}`}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 24,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Box>
        ) : (
          /* ---- FILE VIEW ---- */
          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>

            {/* File Info Card */}
            <Paper
              elevation={0}
              className="scale-in"
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                border: `1px solid ${
                  f.status === 'success' ? alpha(theme.palette.success.main, 0.25)
                  : f.status === 'error' ? alpha(theme.palette.error.main, 0.25)
                  : alpha(theme.palette.primary.main, f.status === 'uploading' ? 0.15 : 0.08)
                }`,
                bgcolor: alpha(theme.palette.background.default, 0.4),
                ...(f.status === 'uploading' && { animation: 'progressGlow 3s ease-in-out infinite' }),
                ...(f.status === 'success' && {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.04)}, ${alpha(theme.palette.background.paper, 0.5)})`,
                }),
              }}
            >
              {/* Top row: icon + name + actions */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48, height: 48, borderRadius: 3, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    ...(f.status === 'success'
                      ? {
                          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.success.main, 0.05)})`,
                          animation: 'successPop 0.4s ease-out',
                        }
                      : f.status === 'error'
                      ? { bgcolor: alpha(theme.palette.error.main, 0.1) }
                      : { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                    ),
                  }}
                >
                  {f.status === 'success' ? (
                    <CheckCircle sx={{ fontSize: 26, color: 'success.main' }} />
                  ) : f.status === 'error' ? (
                    <ErrorOutline sx={{ fontSize: 26, color: 'error.main' }} />
                  ) : (
                    <Archive sx={{ fontSize: 26, color: 'primary.main' }} />
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600} noWrap sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {f.file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                    {fmtSize(f.file.size)}
                    {f.status === 'error' && (
                      <Typography component="span" variant="caption" sx={{ color: 'error.main', ml: 1 }}>
                        {f.error}
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {f.status !== 'uploading' && (
                  <IconButton size="small" onClick={reset} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                    <Close sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
              </Stack>

              {/* Upload Progress */}
              {f.status === 'uploading' && (
                <Box sx={{ mt: 3 }}>
                  {/* Percentage large */}
                  <Typography
                    variant="h3"
                    align="center"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '2rem', sm: '2.5rem' },
                      background: 'linear-gradient(135deg, #7C6FFF, #FF6B8A)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1.5,
                      lineHeight: 1,
                    }}
                  >
                    {p.percentage}%
                  </Typography>

                  {/* Progress bar */}
                  <Box sx={{ position: 'relative' }}>
                    <LinearProgress
                      variant="determinate"
                      value={p.percentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          background: 'linear-gradient(90deg, #7C6FFF, #9D7BFF, #FF6B8A)',
                          transition: 'transform 0.15s linear',
                        },
                      }}
                    />
                  </Box>

                  {/* Stats chips */}
                  <Stack direction="row" justifyContent="center" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<CloudUpload sx={{ fontSize: 14 }} />}
                      label={`${fmtSize(p.bytesUploaded)} / ${fmtSize(p.totalBytes)}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: alpha(theme.palette.divider, 0.5) }}
                    />
                    <Chip
                      icon={<Speed sx={{ fontSize: 14 }} />}
                      label={fmtSpeed(p.speed)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: alpha(theme.palette.divider, 0.5) }}
                    />
                    <Chip
                      icon={<Timer sx={{ fontSize: 14 }} />}
                      label={fmtETA(p.totalBytes - p.bytesUploaded, p.speed)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: alpha(theme.palette.divider, 0.5) }}
                    />
                  </Stack>
                </Box>
              )}

              {/* Success state */}
              {f.status === 'success' && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mb: 0.5 }}>
                    Upload complete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtSize(f.file.size)} uploaded successfully
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Upload Options Box -- always visible */}
            {f.status === 'waiting' && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  bgcolor: alpha(theme.palette.background.default, 0.3),
                }}
              >
                {isAuthenticated ? (
                  <>
                    {/* Retention Period */}
                    <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem', color: 'text.secondary' }}>
                      Retention Period
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {[
                        { value: '1d', label: '1 Day', icon: '24h' },
                        { value: '7d', label: '7 Days', icon: '7d' },
                        { value: '30d', label: '30 Days', icon: '30d' },
                        { value: 'never', label: 'Forever', icon: '∞' },
                      ].map((opt) => {
                        const selected = expiryTime === opt.value;
                        return (
                          <Box
                            key={opt.value}
                            onClick={() => setExpiryTime(opt.value)}
                            sx={{
                              flex: { xs: '1 1 calc(50% - 6px)', sm: '1 1 0' },
                              minWidth: 0,
                              py: 1.5,
                              px: 1,
                              borderRadius: 3,
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              border: `1.5px solid ${selected ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.5)}`,
                              bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                              '&:hover': {
                                borderColor: alpha(theme.palette.primary.main, 0.4),
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                fontWeight: 800,
                                color: selected ? 'primary.main' : 'text.disabled',
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {opt.icon}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                color: selected ? 'primary.main' : 'text.secondary',
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>

                    {/* One-time download */}
                    <Box
                      onClick={() => setOneTimeDownload(!oneTimeDownload)}
                      sx={{
                        mt: 2,
                        py: 1.5,
                        px: 2,
                        borderRadius: 3,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: `1.5px solid ${oneTimeDownload ? alpha(theme.palette.warning.main, 0.4) : alpha(theme.palette.divider, 0.5)}`,
                        bgcolor: oneTimeDownload ? alpha(theme.palette.warning.main, 0.06) : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: alpha(theme.palette.warning.main, 0.3),
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '1rem' }}>💣</Typography>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: oneTimeDownload ? 'warning.main' : 'text.primary' }}>
                            Self-destruct
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            File deleted after first download
                          </Typography>
                        </Box>
                      </Stack>
                      <Switch
                        checked={oneTimeDownload}
                        size="small"
                        color="warning"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setOneTimeDownload(e.target.checked)}
                      />
                    </Box>
                  </>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
                    <Timer sx={{ fontSize: 20, color: 'text.disabled' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                        Expires in 24 hours
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                        Sign in for custom retention &amp; self-destruct
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Paper>
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3.5 }}>
              {f.status === 'waiting' && (
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  startIcon={<CloudUpload />}
                  size="large"
                  sx={{ px: { xs: 4, sm: 6 }, py: 1.5, fontSize: '0.95rem' }}
                >
                  Upload File
                </Button>
              )}
              {f.status === 'success' && (
                <>
                  <Button
                    onClick={copyLink}
                    startIcon={<ContentCopy />}
                    variant="contained"
                    sx={{ px: 3 }}
                  >
                    Copy Download Link
                  </Button>
                  <Button onClick={reset} variant="outlined" sx={{ px: 3 }}>
                    Upload Another
                  </Button>
                </>
              )}
              {f.status === 'error' && (
                <>
                  <Button variant="contained" onClick={handleUpload} startIcon={<Replay />} sx={{ px: 3 }}>
                    Retry Upload
                  </Button>
                  <Button onClick={reset} variant="outlined" sx={{ px: 3 }}>
                    Choose Different File
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Features */}
      {!f && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mt: { xs: 4, sm: 6 }, maxWidth: 620, mx: 'auto' }}
          className="fade-in"
        >
          {[
            { icon: '🔒', title: 'Secure', desc: 'End-to-end encrypted transfer' },
            { icon: '⚡', title: 'Fast', desc: 'Chunked parallel uploads' },
            { icon: '⏰', title: 'Auto-expire', desc: 'Files deleted automatically' },
          ].map((feat) => (
            <Paper
              key={feat.title}
              elevation={0}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                textAlign: 'center',
                transition: 'all 0.25s',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>{feat.icon}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>{feat.title}</Typography>
              <Typography variant="caption" color="text.secondary">{feat.desc}</Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default FileUploadArea;
